import { useState, useEffect, useCallback } from 'react';
import { AiPlanRecord } from '../components/AiPlanDetail';
import { AiPlanContent } from '../components/AiPlanBlockRenderer';

export interface AiPlansFilters {
  search_word?: string;
  author?: string;
  [key: string]: any;
}

export const DEFAULT_AI_PLANS_FILTERS: AiPlansFilters = {
  search_word: '',
  author: '',
};

export interface SortConfig {
  field: string;
  order: 'asc' | 'desc';
}

// TODO: バックエンド実装後に Cloud Functions の URL に差し替える
// const GET_AI_PLANS_URL = 'https://asia-northeast1-<project>.cloudfunctions.net/get_ai_plans';

// ============================================================
// 開発用モックデータ（バックエンド未実装のため暫定）
// ============================================================
const MOCK_CONTENT: AiPlanContent = {
  schema_version: '1.0',
  blocks: [
    { type: 'heading', level: 2, text: '実装プラン概要' },
    {
      type: 'paragraph',
      spans: [
        { text: '本改修の目的は ' },
        { text: 'client_id の必須化', marks: ['bold', 'color:#c62828'] },
        { text: ' です。' },
      ],
    },
    {
      type: 'callout',
      variant: 'warning',
      spans: [{ text: '既存データのマイグレーションが必要です', marks: ['bold'] }],
    },
    {
      type: 'table',
      headers: ['項目', '現状', '改修後'],
      rows: [
        ['バリデーション', 'なし', '必須'],
        ['エラー表示', '汎用', '詳細メッセージ'],
      ],
    },
    {
      type: 'list',
      ordered: true,
      items: [
        { spans: [{ text: 'サービス層に検証関数を追加' }] },
        { spans: [{ text: 'ハンドラーで呼び出し' }] },
        { spans: [{ text: 'ユニットテスト追加' }] },
      ],
    },
    {
      type: 'key_value',
      items: [
        { key: '想定工数', value: '0.5人日' },
        { key: '影響範囲', value: 'sfa_write.py のみ' },
      ],
    },
  ],
};

const MOCK_DATA: AiPlanRecord[] = [
  {
    id: 1,
    planned_date: '2026-05-01',
    author: 'AI:claude-opus-4-7',
    goal: '案件フェーズ昇格時のバリデーション強化',
    resources: 'https://example.com/spec',
    file_paths: ['functions-python/services/sfa_write.py'],
    content: MOCK_CONTENT,
    conversation_id: 'conv-abc-123',
    created_at: '2026-05-01T10:00:00Z',
    updated_at: '2026-05-01T10:00:00Z',
  },
];

export const useAiPlansTable = (
  initialFilters: AiPlansFilters = DEFAULT_AI_PLANS_FILTERS,
  initialSort: SortConfig = { field: 'planned_date', order: 'desc' }
) => {
  const [data, setData] = useState<AiPlanRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<AiPlansFilters>(initialFilters);
  const [sort, setSort] = useState<SortConfig>(initialSort);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: バックエンド実装後に下記の axios リクエストへ差し替える
      // const response = await axios.post(GET_AI_PLANS_URL, {
      //   page, pageSize, filters,
      //   sortField: sort.field, sortOrder: sort.order,
      // });
      // if (response.data.status === 'success') {
      //   setData(response.data.rowsResult || []);
      //   setTotalCount(response.data.total || 0);
      // }

      await new Promise((r) => setTimeout(r, 200));
      setData(MOCK_DATA);
      setTotalCount(MOCK_DATA.length);
    } catch (error) {
      console.error('Error fetching AI plans:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, sort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilters = useCallback((newFilters: AiPlansFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: string) => {
    setSort((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage,
    setPageSize,
    updateFilters,
    handleSort,
    refresh: fetchData,
  };
};
